import React, { useState, useEffect } from 'react';
import { FaTimes, FaFileInvoiceDollar, FaPaperPlane } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { PaymentMilestone } from '../services/OfferService';
import InvoiceService from '../services/InvoiceService';

interface MilestoneInvoiceModalProps {
  isOpen: boolean;
  milestone: PaymentMilestone | null;
  partnerEmail: string;
  onClose: () => void;
  onCreated: (invoiceId: number) => void;
}

type CreateMode = 'draft' | 'send';

export default function MilestoneInvoiceModal({
  isOpen,
  milestone,
  partnerEmail,
  onClose,
  onCreated,
}: MilestoneInvoiceModalProps): React.JSX.Element | null {
  const [mode, setMode] = useState<CreateMode>('draft');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen && milestone) {
      setMode('draft');
      setRecipientEmail(partnerEmail || '');
      setSubject(`Rechnung: ${milestone.label}`);
      setMessage(getDefaultMessage(milestone));
    }
  }, [isOpen, milestone, partnerEmail]);

  const getDefaultMessage = (ms: PaymentMilestone): string => {
    const amount = ms.amount_gross != null
      ? OfferService.formatCurrency(ms.amount_gross)
      : `${ms.percentage}%`;

    return `Sehr geehrte Damen und Herren,

anbei erhalten Sie die Rechnung fuer "${ms.label}" in Hoehe von ${amount}.

Die Rechnung finden Sie im Anhang.

Bei Fragen stehen wir Ihnen gerne zur Verfuegung.

Mit freundlichen Gruessen`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!milestone?.id) {
      toast.error('Kein Meilenstein ausgewaehlt');
      return;
    }

    if (mode === 'send' && !recipientEmail.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    setCreating(true);
    try {
      const result = await OfferService.createInvoiceFromMilestone(milestone.id);
      const invoiceId = (result as any).invoice_id || (result as any).id;

      if (mode === 'send' && invoiceId) {
        try {
          await InvoiceService.sendEmail(invoiceId, {
            recipient_email: recipientEmail,
            subject,
            message,
          });
          toast.success(`Rechnung erstellt und an ${recipientEmail} gesendet`);
        } catch (emailErr: any) {
          console.error('Failed to send invoice email:', emailErr);
          toast.warning('Rechnung erstellt, aber E-Mail konnte nicht gesendet werden');
        }
      } else {
        toast.success(`Rechnung fuer "${milestone.label}" erstellt`);
      }

      onCreated(invoiceId);
    } catch (err: any) {
      console.error('Failed to create invoice from milestone:', err);
      const errorMessage = err?.response?.data?.detail || 'Rechnung konnte nicht erstellt werden';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen || !milestone) return null;

  const amount = milestone.amount_gross != null
    ? OfferService.formatCurrency(milestone.amount_gross)
    : `${milestone.percentage}%`;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '550px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            Rechnung aus Meilenstein erstellen
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div
            style={{
              padding: '16px',
              background: '#f0fdf4',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #bbf7d0',
            }}
          >
            <div style={{ fontSize: '14px', color: '#166534', marginBottom: '8px' }}>
              <strong>Meilenstein:</strong> {milestone.label}
            </div>
            <div style={{ fontSize: '14px', color: '#166534', marginBottom: '8px' }}>
              <strong>Typ:</strong> {OfferService.getMilestoneTypeLabel(milestone.milestone_type)}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669' }}>
              {amount}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Aktion waehlen</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label
                style={{
                  flex: 1,
                  padding: '16px',
                  border: mode === 'draft' ? '2px solid #7c3aed' : '1px solid #e0e3ef',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: mode === 'draft' ? '#f5f3ff' : 'white',
                }}
              >
                <input
                  type="radio"
                  name="mode"
                  value="draft"
                  checked={mode === 'draft'}
                  onChange={() => setMode('draft')}
                  style={{ display: 'none' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaFileInvoiceDollar size={16} color={mode === 'draft' ? '#7c3aed' : '#666'} />
                  <span style={{ fontWeight: 600, color: mode === 'draft' ? '#7c3aed' : '#333' }}>
                    Entwurf erstellen
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  Rechnung wird als Entwurf gespeichert
                </p>
              </label>

              <label
                style={{
                  flex: 1,
                  padding: '16px',
                  border: mode === 'send' ? '2px solid #059669' : '1px solid #e0e3ef',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: mode === 'send' ? '#f0fdf4' : 'white',
                }}
              >
                <input
                  type="radio"
                  name="mode"
                  value="send"
                  checked={mode === 'send'}
                  onChange={() => setMode('send')}
                  style={{ display: 'none' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaPaperPlane size={16} color={mode === 'send' ? '#059669' : '#666'} />
                  <span style={{ fontWeight: 600, color: mode === 'send' ? '#059669' : '#333' }}>
                    Erstellen & senden
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  Rechnung wird erstellt und per E-Mail versendet
                </p>
              </label>
            </div>
          </div>

          {mode === 'send' && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>
                  Empfaenger E-Mail <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="kunde@beispiel.de"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Betreff</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Betreff der E-Mail"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Nachricht</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  placeholder="Ihre Nachricht..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={creating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                border: 'none',
                borderRadius: '10px',
                background: creating ? '#ccc' : mode === 'send' ? '#059669' : '#7c3aed',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: creating ? 'not-allowed' : 'pointer',
              }}
            >
              {mode === 'send' ? <FaPaperPlane size={14} /> : <FaFileInvoiceDollar size={14} />}
              {creating
                ? 'Wird erstellt...'
                : mode === 'send'
                  ? 'Erstellen & senden'
                  : 'Entwurf erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};
